import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import '../css/home.css';
import Initiatives from "../components/Initiatives";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import Landing from "../components/Landing";
import Funds from "../components/Funds";
import BackToTop from "../components/BackToTop";
import { FaHandsHelping, FaHammer, FaArrowRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import * as contentService from "../services/contentService";

const DEFAULT_HERO_VID = "https://firebasestorage.googleapis.com/v0/b/lovingarms.firebasestorage.app/o/videos%2Fkids.mp4?alt=media&token=241edef0-cf6a-4311-a8fe-5cbf316a6641";
const DEFAULT_BUILDING_VID = "https://firebasestorage.googleapis.com/v0/b/lovingarms.firebasestorage.app/o/videos%2Fbuilding1.mp4?alt=media&token=1ae89c20-97fd-4428-bb04-6cdad947a4c4";

export default function Home() {
    const navigate = useNavigate();
    const [heroData, setHeroData] = useState(null);
    const [constructData, setConstructData] = useState(null);

    useEffect(() => {
        const loadCmsData = async () => {
            try {
                const [h, c] = await Promise.all([
                    contentService.getHeroContent(),
                    contentService.getConstructionContent()
                ]);
                if (h) setHeroData(h);
                if (c) setConstructData(c);
            } catch (err) {
                console.error("Error loading home CMS data:", err);
            }
        };
        loadCmsData();
    }, []);

    const heroVid = heroData?.videoUrl || DEFAULT_HERO_VID;
    const badgeText = heroData?.badgeText || "Caring For Every Child";
    const headline = heroData?.headline || "From Streets To";
    const highlightText = heroData?.highlightText || "Stars";
    const subheading = heroData?.subheading || "Dedicated to uplifting underprivileged children in Rajampeta — providing loving shelter, nutritious meals, and quality education.";
    const stats = heroData?.stats && heroData.stats.length > 0 ? heroData.stats : [
        { num: "30+", label: "Children Housed" },
        { num: "15+", label: "Years of Care" },
        { num: "100%", label: "Non-Profit" },
        { num: "24/7", label: "Loving Support" }
    ];

    const constructVid = constructData?.videoUrl || DEFAULT_BUILDING_VID;
    const constructTitle = constructData?.heroTitle || "Laying the Foundation for a Permanent Haven";
    const constructDesc = constructData?.heroDescription || "Our permanent ground in Rajampeta is under construction. We are raising our primary hall and permanent slab so kids never face displacement again. Every brick, sack of cement, and roof beam makes a lifelong difference.";
    const progressLabel = constructData?.progressLabel || "Phase 2: Pillar & Slab Work";
    const progressPercent = constructData?.progressPercent ?? 65;

    return (
        <>
            <Navbar />
            
            {/* Cinematic Hero Section - 100% visible video, no blocking card */}
            <header className="modern-hero-viewport">
                <video
                    key={heroVid}
                    className="hero-video-bg"
                    src={heroVid}
                    poster={process.env.PUBLIC_URL + "/hero-poster.webp"}
                    type="video/mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                >
                    Sorry, your browser doesn't support videos.
                </video>
                <div className="hero-cinematic-overlay"></div>
                
                <div className="container hero-content-wrapper text-center">
                    <div className="hero-text-content">
                        <div className="hero-badge-pill">
                            <span className="live-pulse"></span> {badgeText}
                        </div>
                        <h1 className="hero-headline">
                            {headline} <span className="highlight-gold">{highlightText}</span>
                        </h1>
                        <p className="hero-subheading">
                            {subheading}
                        </p>
                        <div className="hero-action-buttons">
                            <button 
                                className="btn-hero-primary"
                                onClick={() => navigate("/donate")}
                            >
                                <FaHandsHelping className="me-2" /> Donate Now
                            </button>
                            <button 
                                className="btn-hero-outline"
                                onClick={() => navigate("/about")}
                            >
                                Our Story <FaArrowRight className="ms-2" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Floating Bottom Quick Stat Bar */}
                <div className="hero-floating-dock d-none d-md-block">
                    <div className="container">
                        <div className="dock-glass-strip">
                            {stats.map((stat, idx) => (
                                <React.Fragment key={idx}>
                                    <div className="dock-stat-item">
                                        <span className="dock-stat-num">{stat.num}</span>
                                        <span className="dock-stat-lbl">{stat.label}</span>
                                    </div>
                                    {idx < stats.length - 1 && <div className="dock-divider"></div>}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </header>

            <Landing />

            <Initiatives />

            {/* Modern Construction Spotlight Bento */}
            <section className="construction-spotlight-section">
                <div className="container">
                    <div className="spotlight-bento-card">
                        <div className="row g-5 align-items-center">
                            <div className="col-lg-6">
                                <span className="badge-tag" style={{ background: 'rgba(253, 190, 51, 0.15)', color: '#FDBE33' }}>
                                    <FaHammer className="me-1" /> Active Infrastructure Project
                                </span>
                                <h2 className="display-6 fw-bold text-white mt-2">
                                    {constructTitle}
                                </h2>
                                <p className="mt-3 text-slate-300" style={{ color: '#cbd5e1', fontSize: '1.05rem', lineHeight: '1.7' }}>
                                    {constructDesc}
                                </p>
                                
                                <div className="mt-4">
                                    <div className="d-flex justify-content-between text-sm fw-bold">
                                        <span style={{ color: '#FDBE33' }}>{progressLabel}</span>
                                        <span className="text-white">{progressPercent}% Progress</span>
                                    </div>
                                    <div className="construction-progress-bar-wrap">
                                        <div className="construction-progress-bar-fill" style={{ width: `${progressPercent}%` }}></div>
                                    </div>
                                </div>

                                <div className="mt-4 pt-2">
                                    <button
                                        onClick={() => navigate("/construction")}
                                        className="btn-modern-primary"
                                    >
                                        <FaHandsHelping className="me-2" /> View Construction Details
                                    </button>
                                </div>
                            </div>

                            <div className="col-lg-6 text-center">
                                <div className="studio-video-frame">
                                    <video
                                        key={constructVid}
                                        src={constructVid}
                                        alt="Construction progress footage"
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        className="studio-video"
                                    />
                                    <div className="studio-badge">
                                        <FaHammer /> Live Site Ground Footage
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Funds />
            <ContactForm />
            <Footer />
            <BackToTop />
        </>
    );
}